import React, { useEffect } from 'react'

import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import classnames from 'classnames'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import ArticlePreviewCard from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import type { InListingBannerProps } from '~/components/atoms/InListingBanner/InListingBanner'
import InListingBanners from '~/components/atoms/InListingBanner/InListingBanner'
import Loader from '~/components/atoms/Loader/Loader'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import useArticles from '~/services/query/use-articles'

import type { MagazineIndexBannerProps } from '../MagazineIndexBanner/MagazineIndexBanner'
import MagazineIndexBanner from '../MagazineIndexBanner/MagazineIndexBanner'
import type { MyPortfolioBannerProps } from '../MyPortfolioBanner/MyPortfolioBanner'
import MyPortfolioBanner from '../MyPortfolioBanner/MyPortfolioBanner'
import NewsletterSignup from '../NewsletterSignup/NewsletterSignup'

import styles from './ArticlesList.module.css'

export interface ArticlesProps {
  fallbackData: ArticlePreviewCardProps[]
  inListingBanners?: InListingBannerProps
  myPortfolioBanner?: MyPortfolioBannerProps
  magazineIndexBanner: MagazineIndexBannerProps
  pageSize: number
  articlesTypeSlug?: string | boolean
  pagenumber?: number
  searchInsightsEvents?: {
    article: TSearchAnalystEventProps
    articleLarge?: TSearchAnalystEventProps
  }
  idFilterList?: string[]
  filterValue?: string
  useRowView?: boolean
}

export default function Articles({
  fallbackData,
  inListingBanners,
  pageSize,
  magazineIndexBanner,
  myPortfolioBanner,
  articlesTypeSlug,
  pagenumber,
  searchInsightsEvents,
  idFilterList,
  useRowView = false,
}: ArticlesProps) {
  const t = useTranslations('common')
  const router = useRouter()

  let total = 0
  let insightsQueryID: string | undefined = undefined
  const { data, error, size, setSize, isValidating } = useArticles(
    articlesTypeSlug,
    fallbackData,
    pageSize,
    idFilterList
  )

  useEffect(() => {
    if (pagenumber) setSize(pagenumber)
  }, [pagenumber])

  // Build the public URL path (converting /edition to /articles)
  const internalPath = router.asPath?.split('?')[0]
  const basePath = internalPath.replace('/edition', '/articles')
  const nextPageUrl = `${basePath}/page/${size + 1}`
  const articles =
    data && data.length
      ? data.reduce((accumulator, value) => {
          total = value.total
          insightsQueryID = value.insightsQueryID
          return accumulator.concat(value.hits)
        }, [] as any[])
      : fallbackData
  const isLoadingInitialData = !data && !error

  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined')

  const loadMoreHandler = (
    e?:
      | React.MouseEvent<HTMLLinkElement, MouseEvent>
      | React.KeyboardEvent<HTMLLinkElement>
  ) => {
    e?.preventDefault()

    // Load more data
    setSize(size + 1)

    // Update URL to create shareable link (without page reload)
    const nextPage = size + 1
    const currentPath = router.asPath.split('?')[0].split('/page/')[0]
    const publicUrl =
      currentPath.replace('/edition', '/articles') + `/page/${nextPage}`

    // Update URL to create shareable link (without page reload)
    // Note: Canonical URL won't update client-side during load more,
    // but will be correct when the paginated URL is visited directly (for SEO)
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', publicUrl)
    }
  }

  const RenderArticleSnippet = ({
    article,
    idx,
  }: {
    article: any
    idx: number
  }) => {
    return (
      <div
        key={`${idx}-${article?.title || 'no-title'}`}
        className={styles.portraitArticleWrap}
      >
        <div className={classnames(styles.articleListWrap)}>
          <ArticlePreviewCard
            {...article}
            insightsQueryID={insightsQueryID}
            imageType="landscape"
            orientation={!useRowView ? 'landscape' : 'portrait'}
            variant={!useRowView ? 'magazineResponsive' : 'default'}
            sizes={'(max-width: 799px) 100vw, 50vw'}
            searchInsightsEvent={searchInsightsEvents?.article}
            testAttr="articles-list-article-preview-card"
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <div
        className={classnames(
          styles.articlesWrap,
          fallbackData &&
            isValidating &&
            size === 1 &&
            styles.articlesWrapLoading,
          useRowView && styles.articlesRowContainer
        )}
      >
        {articles.map((article, idx) => {
          if (idx === 10 && myPortfolioBanner) {
            return (
              <div key={`${idx}-in-my-portfolio-banner`}>
                <div className={styles.inListingBannersWrap}>
                  <MyPortfolioBanner {...myPortfolioBanner} />
                </div>
              </div>
            )
          }
          if (idx === 10 && inListingBanners) {
            return (
              <div key={`${idx}-in-listing`}>
                <div className={styles.inListingBannersWrap}>
                  <InListingBanners {...inListingBanners} />
                </div>
                <RenderArticleSnippet article={article} idx={idx} />
              </div>
            )
          }

          if (idx === 7 && inListingBanners) {
            return (
              <div key={`${idx}-news-letter-signup`}>
                <NewsletterSignup
                  title="Signup"
                  body="Get expert insights straight to your inbox. Find out about new releases & articles first in our weekly newsletter. "
                  image={{
                    alt: 'Signup',
                    asset:
                      'https://cdn.sanity.io/images/dqllnil6/production/39e32deb024e812ef293cfd45fc697bf90701fc7-1850x990.jpg',
                    credit: {
                      text: null,
                      theme: 'light',
                    },
                  }}
                />
                <Spacer size={SpacerSizes.lg} />
                <RenderArticleSnippet article={article} idx={idx} />
              </div>
            )
          }

          if (idx === 4 && magazineIndexBanner) {
            return (
              <div key={`${idx}-mag-banner`}>
                <MagazineIndexBanner {...magazineIndexBanner} />
                <Spacer size={SpacerSizes.lg} />
                <RenderArticleSnippet article={article} idx={idx} />
              </div>
            )
          }
          return <RenderArticleSnippet article={article} idx={idx} key={idx} />
        })}
      </div>
      {isLoadingMore && (
        <div className={styles.artworks}>
          <Loader />
        </div>
      )}

      {articles.length < total && (
        <div className={styles.btnWrap}>
          <Button
            preventDefault
            iconLeft
            linkOnclick={loadMoreHandler}
            link={nextPageUrl}
            variant={ButtonVariants.secondaryDark}
            testAttr="load-more-articles"
          >
            {t('loadMore')}
          </Button>
        </div>
      )}
    </div>
  )
}
